import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  EMPTY,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  Subject,
  Subscription,
  take,
  tap,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ConfirmDialogComponent } from '@shared/components';
import {
  DataRefreshService,
  RoutingService,
  SignalRService,
  ToastNotificationService,
} from '@shared/services';
import {
  Constants,
  convertDateToJsonDateFormat,
  getStaticMessage,
  toUTCDate,
} from '@shared/utils';
import {
  DigitalSignatureModalResult,
  DigitalSignatureResult,
  DprReportSignRequestStatus,
  Signer,
} from '@dpr/models/digital-signature';
import {
  DprService,
  DprCardService,
  DigitalSignatureService,
  DprApproversService,
} from '@dpr/services';
import {
  StaticMessage,
  ExceptionMessage,
  UtcDateTimeOptions,
} from '@shared/enums';
import { OfflineFacade, UserDetailsFacade } from '@shared/state/facades';
import { Attachment, DprApprover, DprOnlineApproval } from '@shared/models/dpr';
import { DisplayESignButtonPipe } from '@dpr/pipes';
import { DigitalSignatureModalComponent } from '@dpr/components';
import { DigitalSignatureAction } from '@dpr/enums';
import { DprModalTitlePipe, DprTitlePipe } from '@shared/pipes';
import {
  Approver,
  OnlineApprover,
  SubprojectConfigurationCustomNames,
} from '@shared/models/project-setup';
import { DprCard } from '@dpr/models';
import { CryptoService } from '@shared/services';

@UntilDestroy()
@Component({
  selector: 'app-dpr-card',
  templateUrl: './dpr-card.component.html',
  styleUrls: ['./dpr-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprCardComponent implements OnInit, OnChanges {
  //#region Inputs

  @Input({ required: true }) data: DprCard;
  @Input({ required: true }) canEdit: boolean;
  @Input({ required: true }) approvers: Approver[];
  @Input({ required: true }) onlineApprovers: OnlineApprover[];
  @Input({ required: true }) accountId: string;
  @Input({ required: true }) clientId: string;
  @Input({ required: true }) clientName: string;
  @Input({ required: true }) isDigitalSignatureEnabled: boolean;
  @Input({ required: true }) canEditExternalPartyComment: boolean;
  @Input({ required: true }) customNames: SubprojectConfigurationCustomNames;
  @Input({ required: true }) isAppOnline: boolean;

  //#endregion

  data$ = new BehaviorSubject<DprCard>(null);
  dprApprovers$ = new BehaviorSubject<DprApprover[]>([]);
  dprApproverSyncStatus$ = new Subject<boolean>();
  dprDetailsActivityStreamId$ =
    this.userDetailsFacade.query.selectedSubproject$.pipe(
      take(1),
      map((x) => x.data?.activityStreams[0].id)
    );
  dprOnlineApprovals$ = new BehaviorSubject<DprOnlineApproval[]>([]);

  utcDate: Date;
  formattedDprDisplayId: string;
  formattedDprDate: string;
  shouldDisplayDprCardTabs = false;
  shouldDisableApprovalTab = false;

  btnEdit = $localize`:@@dpr.dprCard.btnEdit:Edit`;
  btnReview = $localize`:@@dpr.dprCard.btnReview:Review`;
  btnView = $localize`:@@dpr.dprCard.btnView:View`;

  private signRequestReportUploadSubscription: Subscription;

  constructor(
    private dialog: MatDialog,
    private dprCardService: DprCardService,
    private dprService: DprService,
    private toastNotificationService: ToastNotificationService,
    private routingService: RoutingService,
    private offlineFacade: OfflineFacade,
    private userDetailsFacade: UserDetailsFacade,
    private displayESignButtonPipe: DisplayESignButtonPipe,
    private signalRService: SignalRService,
    private dprTitlePipe: DprTitlePipe,
    private dprApproversService: DprApproversService,
    private digitalSignatureService: DigitalSignatureService,
    private dataRefreshService: DataRefreshService,
    private cryptoService: CryptoService,
    private dprModalTitlePipe: DprModalTitlePipe
  ) {}

  ngOnInit(): void {
    if (this.data.hasActiveSignRequest && !this.data.isSignDprDetailsUploaded) {
      this.registerNotification();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.shouldDisplayDprCardTabs =
      this.approvers?.length > 0 || this.onlineApprovers?.length > 0;

    if (
      this.data.dprOnlineApprovals.length < 1 &&
      this.onlineApprovers?.every(
        (onlineApprover: OnlineApprover) => !onlineApprover.canApprove
      ) &&
      this.approvers?.every((a: Approver) => !a.isActive)
    ) {
      this.shouldDisableApprovalTab = true;
    }

    if (changes?.data?.currentValue) {
      this.data$.next(changes.data.currentValue);

      this.utcDate = toUTCDate(
        this.data.dateTime,
        UtcDateTimeOptions.UtcDateWithoutTime
      );

      this.formattedDprDisplayId = this.dprTitlePipe.transform(
        this.data.displayId
      );
      this.formattedDprDate = this.dprCardService.formatDprDate(
        this.data.dateTime
      );

      this.dprApprovers$.next(changes.data.currentValue.dprApprovers);

      this.dprOnlineApprovals$.next(
        changes.data.currentValue.dprOnlineApprovals
      );
    }
  }

  goToDprDetails(dprDetailId: number | string): void {
    this.dprDetailsActivityStreamId$
      .pipe(
        untilDestroyed(this),
        take(1),
        tap((activityStreamId: number) => {
          this.routingService.navigateToRoute(
            this.routingService.routePrefix,
            'dpr-details',
            this.cryptoService.encryptId(this.data.subprojectId),
            `${dprDetailId}`,
            'activities',
            `${activityStreamId}`
          );
        })
      )
      .subscribe();
  }

  confirmLockDPR(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      ...Constants.DialogOptions,
      data: {
        extra: `#${this.formattedDprDisplayId}`,
        title: $localize`:@@dpr.dprCard.titleLockDpr:Lock DPR`,
        message: $localize`:@@dpr.dprCardDetails.txtProceedWithLocking:Proceed with locking the DPR? A signed DPR can be uploaded after locking.`,
        buttonLabel: $localize`:@@dpr.dprCard.btnLock:Lock`,
        buttonIcon: 'lock',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter((result) => result),
        tap(() => {
          this.lockDpr();
        })
      )
      .subscribe();
  }

  lockDpr(): void {
    const lockDate = new Date();
    const lockDateString = convertDateToJsonDateFormat(lockDate);

    this.dprService
      .lockDpr(this.data.dprDetailId, this.data.subprojectId, lockDateString)
      .pipe(
        untilDestroyed(this),
        tap((lockedDprDetails) => {
          // Update the locked DPR in local storage with the full data returned from API
          this.offlineFacade.updateExistingDprDetails(lockedDprDetails);

          this.data$.next({
            ...this.data$.value,
            dprDetailLockedDate: lockDate,
            isSignDprDetailsUploaded: false,
            dprOnlineApprovals: this.dprOnlineApprovals$.value,
          });

          const notificationMessage =
            `DPR #${this.formattedDprDisplayId}, ` +
            `${this.formattedDprDate} ` +
            $localize`:@@dpr.dprCardDetails.txtWasLocked:was locked`;
          this.toastNotificationService.success(notificationMessage);
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 400) {
            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
              ...Constants.DialogOptions,
              data: {
                extra: `#${this.formattedDprDisplayId}`,
                title: $localize`:@@dpr.dprCard.titleLockDprError:Lock DPR error.`,
                message: $localize`:@@dpr.dprCard.txtLockDprErrorIncomplete:The DPR could not be locked, as it is incomplete on the server.</br></br> Click <b>OK</b> to refresh.`,
                buttonLabel: getStaticMessage(StaticMessage.OkButton),
                hideCancelButton: true,
              },
            });

            dialogRef
              .afterClosed()
              .pipe(
                untilDestroyed(this),
                tap(() => {
                  this.dataRefreshService.setRefreshNeeded();
                })
              )
              .subscribe();

            return of(EMPTY);
          }
          if (error.status === 409) {
            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
              ...Constants.DialogOptions,
              data: {
                extra: `#${this.formattedDprDisplayId}`,
                title: $localize`:@@dpr.dprCard.titleLockDprError:Lock DPR error.`,
                message: $localize`:@@dpr.dprCard.txtLockDprErrorAlreadyLocked:The DPR could not be locked, as the current revision of the DPR is already locked on the server.</br></br> Click <b>OK</b> to refresh.`,
                buttonLabel: getStaticMessage(StaticMessage.OkButton),
                hideCancelButton: true,
              },
            });

            dialogRef
              .afterClosed()
              .pipe(
                untilDestroyed(this),
                tap(() => {
                  this.dataRefreshService.setRefreshNeeded();
                })
              )
              .subscribe();

            return of(EMPTY);
          }

          this.dialog.open(ConfirmDialogComponent, {
            ...Constants.DialogOptions,
            data: {
              extra: `#${this.formattedDprDisplayId}`,
              title: $localize`:@@dpr.dprCard.titleLockDprError:Lock DPR error.`,
              message: $localize`:@@dpr.dprCard.txtLockDprErrorTryAgain:An error has occurred while locking DPR, please try again.`,
              buttonLabel: getStaticMessage(StaticMessage.OkButton),
              hideCancelButton: true,
            },
          });
          return of(EMPTY);
        })
      )
      .subscribe();
  }

  confirmUnlock(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      ...Constants.DialogOptions,
      data: {
        extra: `#${this.formattedDprDisplayId}`,
        title: $localize`:@@dpr.dprCard.titleUnlockAndEditDpr:Unlock & Edit DPR`,
        message: $localize`:@@dpr.dprCard.txtUnlockAndCreateRevision:You are going to unlock this DPR. A new revision will be created and all data from the old revision will be copied to the new revision.`,
        buttonLabel: $localize`:@@dpr.dprCard.btnUnlockAndEdit:Unlock & Edit`,
        buttonIcon: 'lock-open-variant',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter((result) => result),
        mergeMap(() => {
          return this.dprService.unlockDpr(this.data.dprDetailId);
        }),
        mergeMap((newDprDetailId) => {
          return combineLatest([
            this.dprService.getLastThreeDprs(),
            of(newDprDetailId),
          ]);
        }),
        take(1),
        tap(([lastThreeDprs, newDprDetailId]) => {
          this.offlineFacade.setDprDetailsList(lastThreeDprs);

          this.goToDprDetails(newDprDetailId);
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 409) {
            const errDialogRef = this.dialog.open(ConfirmDialogComponent, {
              ...Constants.DialogOptions,
              data: {
                extra: `#${this.formattedDprDisplayId}`,
                title: $localize`:@@dpr.dprCard.titleUnlockDprError:Unlock DPR error.`,
                message: $localize`:@@dpr.dprCard.txtUnlockDprErrorAlreadyUnlocked:The DPR could not be unlocked, as the current revision of the DPR is already unlocked on the server.</br></br> Click <b>OK</b> to refresh.`,
                buttonLabel: getStaticMessage(StaticMessage.OkButton),
                hideCancelButton: true,
              },
            });

            errDialogRef
              .afterClosed()
              .pipe(
                untilDestroyed(this),
                tap(() => {
                  this.dataRefreshService.setRefreshNeeded();
                })
              )
              .subscribe();

            return of(EMPTY);
          }

          this.dialog.open(ConfirmDialogComponent, {
            ...Constants.DialogOptions,
            data: {
              extra: `#${this.formattedDprDisplayId}`,
              title: $localize`:@@dpr.dprCard.titleUnlockDprError:Unlock DPR error.`,
              message: $localize`:@@dpr.dprCard.txtUnlockDprErrorTryAgain:An error has occurred while unlocking DPR, please try again.`,
              buttonLabel: getStaticMessage(StaticMessage.OkButton),
              hideCancelButton: true,
            },
          });
          return of(EMPTY);
        })
      )
      .subscribe();
  }

  onOpenDigitalSignatureModal(): void {
    const existingSignRequestData$: Observable<DprReportSignRequestStatus> =
      this.data$.value.hasActiveSignRequest
        ? this.digitalSignatureService.getDprSignRequestStatus(
            this.data$.value.dprReportSignRequestId
          )
        : of(null);

    const previousSigners$: Observable<Signer[]> =
      !this.data.isFirstDpr && !this.data$.value.hasActiveSignRequest
        ? this.digitalSignatureService.copySigners(this.data.dateTime)
        : of([]);

    this.openDigitalSignatureModal(
      this.data$.value,
      existingSignRequestData$.pipe(shareReplay()),
      previousSigners$.pipe(shareReplay())
    )
      .pipe(
        untilDestroyed(this),
        filter((result) => !!result),
        tap((result) => {
          this.data$.next({
            ...this.data$.value,
            hasActiveSignRequest: result.hasActiveSignRequest,
            dprReportSignRequestId:
              result.dprReportSignRequestId ??
              this.data$.value.dprReportSignRequestId,
          });

          if (result.hasActiveSignRequest === true) {
            this.registerNotification();
          } else {
            this.unregisterNotification();
          }
        })
      )
      .subscribe();
  }

  openDigitalSignatureModal(
    dprCard: DprCard,
    existingSignRequestData$: Observable<DprReportSignRequestStatus>,
    previousSigners$: Observable<Signer[]>
  ): Observable<DigitalSignatureResult> {
    const dialogRef = this.dialog.open(DigitalSignatureModalComponent, {
      ...Constants.DialogOptions,
      panelClass: 'dialogCustom--largeSize',
      data: {
        dprCard,
        title: this.dprModalTitlePipe.transform(
          this.dprCardService.getModalTitle(
            dprCard.displayId,
            dprCard.dateTime,
            dprCard.dprDetailRevision
          ),
          $localize`:@@dpr.dprCard.txtESign:E-Sign`
        ),
        existingSignRequestData$,
        previousSigners$,
      },
    });

    return dialogRef.afterClosed().pipe(
      untilDestroyed(this),
      mergeMap((dialogResult: DigitalSignatureModalResult) => {
        switch (dialogResult?.action) {
          case DigitalSignatureAction.Send:
            return this.digitalSignatureService.sendDprSignRequest({
              dprDetailId: dprCard.dprDetailId,
              reportOptions: {
                addWatermark: false,
                imageCompressionType: dialogResult.imageCompressionType,
              },
              signerEmails: dialogResult.emails,
            });
          case DigitalSignatureAction.Cancel:
            return this.digitalSignatureService.cancelDprSignRequest(
              dprCard.dprReportSignRequestId
            );
          default:
            return of(null);
        }
      })
    );
  }

  addDprApprover(dprApprover: DprApprover): void {
    this.dprApproversService
      .createDprApprover(dprApprover, this.data.subprojectId)
      .pipe(
        tap((result) => {
          this.offlineFacade.addDprApprover(this.data.dprDetailId, result);
          this.dprApprovers$.next([...this.dprApprovers$.value, result]);
          this.dprApproverSyncStatus$.next(true);
        }),
        catchError((error) => {
          console.error('Error creating DPR approver', error);
          this.dprApproverSyncStatus$.next(false);
          return EMPTY;
        })
      )
      .subscribe();
  }

  updateDprApprover(dprApprover: DprApprover): void {
    this.dprApproversService
      .updateDprApprover(dprApprover, this.data.subprojectId)
      .pipe(
        tap((result) => {
          this.offlineFacade.updateDprApprover(
            this.data.dprDetailId,
            dprApprover
          );
          this.dprApprovers$.next(
            this.dprApprovers$.value.map((x) => {
              return x.id === dprApprover.id ? { ...x, name: result.name } : x;
            })
          );
          this.dprApproverSyncStatus$.next(true);
        }),
        catchError((error) => {
          console.error('Error updating DPR approver', error);
          this.dprApproverSyncStatus$.next(false);
          return EMPTY;
        })
      )
      .subscribe();
  }

  deleteDprApprover(dprApprover: DprApprover): void {
    this.dprApproversService
      .deleteDprApprover(
        dprApprover.id,
        this.data.subprojectId,
        this.data.dprDetailId
      )
      .pipe(
        tap(() => {
          this.offlineFacade.deleteDprApprover(
            this.data.dprDetailId,
            dprApprover.id
          );
          this.dprApprovers$.next(
            this.dprApprovers$.value.filter((x) => x.id !== dprApprover.id)
          );
          this.dprApproverSyncStatus$.next(true);
        }),
        catchError((error) => {
          console.error('Error deleting DPR approver', error);
          this.dprApproverSyncStatus$.next(false);
          return EMPTY;
        })
      )
      .subscribe();
  }

  syncOnlineApproval(dprOnlineApproval: DprOnlineApproval): void {
    this.dprApproversService
      .syncDprOnlineApproval(dprOnlineApproval, this.data.subprojectId)
      .pipe(
        tap((result) => {
          this.offlineFacade.updateDprOnlineApproval(
            this.data.dprDetailId,
            result.dprOnlineApproval
          );

          const dprOnlineApprovalsValue = this.dprOnlineApprovals$.value;
          if (
            dprOnlineApprovalsValue.find((x) => x.id === dprOnlineApproval?.id)
          ) {
            this.dprOnlineApprovals$.next(
              this.dprOnlineApprovals$.value.map((x) =>
                x.id === dprOnlineApproval?.id
                  ? { ...x, comment: result.dprOnlineApproval.comment }
                  : x
              )
            );
          } else {
            this.dprOnlineApprovals$.next([
              ...this.dprOnlineApprovals$.value,
              result.dprOnlineApproval,
            ]);
          }

          this.data$.next({
            ...this.data$.value,
            dprDetailsApproveStatus: result.dprDetail?.approveStatus,
          });
        }),
        catchError((error: HttpErrorResponse) => {
          if (
            error.error?.exceptionMessage ===
            ExceptionMessage.UnlockedOrNotLatestDpr
          ) {
            const errDialogRef = this.dialog.open(ConfirmDialogComponent, {
              ...Constants.DialogOptions,
              data: {
                extra: `#${this.formattedDprDisplayId}`,
                title: $localize`:@@dpr.dprCard.titleSyncOnlineApprovalError:Sync online approval error.`,
                message: $localize`:@@dpr.dprCard.txtSyncOnlineApprovalErrorNotLockedOrLatestRevision:Cannot synchronize the online approval as the DPR is not the latest revision or the current revision of the DPR is not locked on the server.`,
                buttonLabel: getStaticMessage(StaticMessage.OkButton),
                hideCancelButton: true,
              },
            });

            errDialogRef
              .afterClosed()
              .pipe(
                untilDestroyed(this),
                tap(() => {
                  this.dataRefreshService.setRefreshNeeded();
                })
              )
              .subscribe();

            return of(EMPTY);
          }

          this.dialog.open(ConfirmDialogComponent, {
            ...Constants.DialogOptions,
            data: {
              extra: `#${this.formattedDprDisplayId}`,
              title: $localize`:@@dpr.dprCard.titleSyncOnlineApprovalError:Sync online approval error.`,
              message: $localize`:@@dpr.dprCard.txtSyncOnlineApprovalErrorTryAgain:An error has occurred while synchronizing the online approval, please try again.`,
              buttonLabel: getStaticMessage(StaticMessage.OkButton),
              hideCancelButton: true,
            },
          });
          return of(EMPTY);
        })
      )
      .subscribe();
  }

  onUploadSignedReport(files: File[]): void {
    this.dprCardService
      .uploadSignedReport(files, this.clientId, this.data)
      .pipe(
        untilDestroyed(this),
        filter((attachment) => !!attachment),
        mergeMap((attachment) => {
          return combineLatest([
            of(attachment),
            this.dprService.uploadSignedDpr(
              this.data.dprDetailId,
              this.data.subprojectId,
              attachment
            ),
          ]);
        }),
        tap(([attachment]) => {
          this.updateDprDetails(attachment);

          this.data$.next({
            ...this.data$.value,
            isSignDprDetailsUploaded: !!attachment,
            // needed for file download
            signedReportExternalUrl: attachment.externalUrl,
            signedReportName: attachment.name,
            signedReportFileType: attachment.type,
          });

          const notificationMessage =
            `DPR #${this.formattedDprDisplayId}, ` +
            `${this.formattedDprDate} - ` +
            $localize`:@@dpr.dprCardDetails.txtSignedReportUploaded:signed report uploaded`;
          this.toastNotificationService.success(notificationMessage);
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 400) {
            const uploadErrorCauses =
              $localize`:@@dpr.dprCardDetails.txtUploadSignedDprErrorCauses:The signed DPR could not be uploaded, as the DPR is incomplete on the server, already has a signed report` +
              (this.displayESignButtonPipe.transform(
                this.data$.value,
                this.isAppOnline,
                this.isDigitalSignatureEnabled
              )
                ? ' ' +
                  $localize`:@@dpr.dprCardDetails.txtUploadSignedDprErrorHasActiveRequest:or it has an active e-sign request`
                : '') +
              '.</br></br>' +
              $localize`:@@dpr.dprCardDetails.txtUploadSignedDprErrorClickOk:Click <b>OK</b> to refresh.`;

            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
              ...Constants.DialogOptions,
              data: {
                extra: `#${this.formattedDprDisplayId}`,
                title: $localize`:@@dpr.dprCardDetails.titleUploadSignedDprError:Upload signed DPR error.`,
                message: uploadErrorCauses,
                buttonLabel: getStaticMessage(StaticMessage.OkButton),
                hideCancelButton: true,
              },
            });

            dialogRef
              .afterClosed()
              .pipe(
                untilDestroyed(this),
                tap(() => {
                  this.dataRefreshService.setRefreshNeeded();
                })
              )
              .subscribe();

            return of(EMPTY);
          }

          this.dialog.open(ConfirmDialogComponent, {
            ...Constants.DialogOptions,
            data: {
              extra: `#${this.formattedDprDisplayId}`,
              title: $localize`:@@dpr.dprCardDetails.titleUploadSignedDprError:Upload signed DPR error.`,
              message: $localize`:@@dpr.dprCardDetails.txtUploadSignedDprErrorTryAgain:An error has occurred while uploading the signed DPR, please try again.`,
              buttonLabel: getStaticMessage(StaticMessage.OkButton),
              hideCancelButton: true,
            },
          });

          return of(EMPTY);
        })
      )
      .subscribe();
  }

  private updateDprDetails(attachment: Attachment): void {
    this.offlineFacade.setDprDetailsSignedReport(
      this.data.dprDetailId,
      attachment
    );
  }

  private registerNotification(): void {
    this.signRequestReportUploadSubscription =
      this.signalRService.signRequestReportUploaded$
        .pipe(
          untilDestroyed(this),
          filter(
            (signRequestReportUploaded) =>
              signRequestReportUploaded.Key === this.data.dprDetailId
          ),
          take(1),
          tap((signRequestReportUploaded) => {
            this.data$.next({
              ...this.data$.value,
              signedReportName: signRequestReportUploaded.FileName,
              signedReportFileType: signRequestReportUploaded.FileType,
              signedReportExternalUrl: signRequestReportUploaded.FileUrl,
              isSignDprDetailsUploaded: true,
            });
          })
        )
        .subscribe();
  }

  private unregisterNotification(): void {
    if (this.signRequestReportUploadSubscription) {
      this.signRequestReportUploadSubscription.unsubscribe();
      this.signRequestReportUploadSubscription = null;
    }
  }
}
